import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useWatch } from "react-hook-form";
import { useNavigate } from "react-router";

import { validate } from "capitalrx-components-2.0";
import { Dropdown, FileUpload, Form, RadioGroup, SubmitButton, TextArea, Textbox, useForm } from "capitalrx-components-2.0/lib/forms";
import { Button, Buttons, Icon, Link, Message, Messages, Modal, Panel, Spinner, useAlert } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading, Regular, Semibold, Subheading, Title } from "capitalrx-components-2.0/lib/typography";
import { DeepMutable, states } from "capitalrx-types";

import {
  createPaymentPreferenceAPI,
  getProviderManagementDocumentsUrlAPI,
  getProviderManagementNetworkConfigListAPI,
  getProviderManagementNetworkListAPI,
  updatePaymentPreferenceAPI,
} from "@/api";
import { getProviderManagementAPI } from "@/api/get";
import { ProviderManagement } from "@/api/interfaces";
import { PROVIDER_MANAGEMENT_LAYOUT } from "@/constants";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";
import {
  formatPhoneWithExtension,
  formatZipCode,
  getThemeButton,
  getThemeURL,
  hexToRgbColor,
  processDocumentFiles,
  transformNetworkData,
  uploadFileToS3,
  validatePhoneNumber,
} from "@/utils";

import { deepEqual } from "../../../../../utils/utils";
import { ProviderPaymentRemittanceDataForm } from "../../components/PaymentRemittance/PaymentRemittanceGroupFields";
import { FieldsBankAccount } from "../../components/PaymentRemittance/PaymentRemittanceGroupFields/FieldsBankAccount";
import { FieldsRemittanceType } from "../../components/PaymentRemittance/PaymentRemittanceGroupFields/FieldsRemittanceType";
import { FieldsTransactionValidation } from "../../components/PaymentRemittance/PaymentRemittanceGroupFields/FieldsTransactionValidation";
import { ProviderNetworkDropdown } from "../../components/PaymentRemittance/PaymentRemittanceGroupFields/ProviderNetworkDropdown";

export const NewNetworkForm: FC = () => {
  const { primary_color, primary_hover_color, secondary_hover_color } = useContext(ThemeSelectorContext);
  const { threeFull, sixFull, twelve } = useResponsive(PROVIDER_MANAGEMENT_LAYOUT);
  const { getAuthToken } = useContext(AuthenticationContext);
  const showAlert = useAlert();
  const navigate = useNavigate();

  const form = useForm<ProviderPaymentRemittanceDataForm>();
  const paymentType = useWatch({
    control: form.control,
    name: "payment_type",
    defaultValue: ProviderManagement.PAYMENT_TYPE_VALUES.ACH,
  });

  // Format financial institution fields: zip code, phone number, fax number
  const [fiZip, fiPhone, fiFax] = useWatch({
    control: form.control,
    name: ["financial_institutions.zip_code", "financial_institutions.phone_number", "financial_institutions.fax_number"],
  });

  // Apply formatting when values change
  useEffect(() => {
    const fiFields = [
      { value: fiZip, name: "financial_institutions.zip_code", formatter: formatZipCode },
      { value: fiPhone, name: "financial_institutions.phone_number", formatter: formatPhoneWithExtension },
      { value: fiFax, name: "financial_institutions.fax_number", formatter: formatPhoneWithExtension },
    ];

    fiFields.forEach(({ value, name, formatter }) => {
      if (value !== undefined) {
        const f = formatter(value as string);
        if (f !== value) {
          (form.setValue as any)(name, f, { shouldValidate: false });
        }
      }
    });
  }, [fiZip, fiPhone, fiFax, form]);

  const [provider, setProvider] = useState<ProviderManagement.ProviderSchema>();
  const [paymentPreference, setPaymentPreference] = useState<ProviderManagement.PaymentPreferenceSchema>();
  const [networkResults, setNetworkResults] = useState<ProviderManagement.ProviderNetworkListSearchResponse>();
  const [networkConfigResults, setNetworkConfigResults] = useState<ProviderManagement.ProviderNetworkConfigListSearchResponse>();
  const [isLoading, setIsLoading] = useState(false);
  const [isNetworkLoading, setIsNetworkLoading] = useState(false);
  const [isNetworkConfigLoading, setIsNetworkConfigLoading] = useState(false);

  const getProvider = useCallback(async () => {
    setIsLoading(true);

    try {
      // Find provider management record and process response
      const pmResponse = await getProviderManagementAPI(getAuthToken());
      setProvider(pmResponse.provider);
      if (pmResponse.payment_preferences?.length) {
        setPaymentPreference(pmResponse.payment_preferences[0].provider_payment_info);
      }

      setIsLoading(false);
    } catch (error) {
      logEvent(ANALYTIC_EVENTS.ERRORS.providerManagementGet);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    getProvider();
  }, []);

  useEffect(() => {
    const fetchNetworks = async () => {
      setIsNetworkLoading(true);
      try {
        const requestPayload = { page_number: 1, results_per_page: 10, order_by: [] };
        const response = await getProviderManagementNetworkListAPI(requestPayload, getAuthToken());
        setNetworkResults(response);
      } catch (error) {
        console.error("Failed to fetch networks:", error);
      } finally {
        setIsNetworkLoading(false);
      }
    };
    const fetchNetworkConfigs = async () => {
      setIsNetworkConfigLoading(true);
      try {
        const requestPayload = { provider_id: provider?.id };
        const response = await getProviderManagementNetworkConfigListAPI(requestPayload, getAuthToken());
        setNetworkConfigResults(response);
      } catch (error) {
        console.error("Failed to fetch network configurations:", error);
      } finally {
        setIsNetworkConfigLoading(false);
      }
    };

    fetchNetworks();
    fetchNetworkConfigs();
  }, [getAuthToken, provider]);

  const networkInfo = transformNetworkData(networkResults);

  const configuredNetworkIds = new Set(
    networkConfigResults?.results?.map((result) => result.provider_network_configuration.provider_network_id) || [],
  );

  const remainingNetworks = networkInfo
    ? networkInfo.filter((network) => !configuredNetworkIds.has(network?.id) && network?.in_network !== false)
    : undefined;

  const submitHandler = async (data: ProviderPaymentRemittanceDataForm): Promise<void> => {
    logEvent(ANALYTIC_EVENTS.PROVIDER_MANAGEMENT.update);

    try {
      const authLetterResult = processDocumentFiles(
        data.authorization_letter.files,
        data.authorization_letter.notes,
        "authorization_letter_for_eft",
      );

      const bankLetterResult = processDocumentFiles(
        data.bank_letterhead_voided_check.files,
        data.bank_letterhead_voided_check.notes,
        data.bank_letterhead_voided_check.type,
      );

      const fileObjects = [...authLetterResult.fileObjects, ...bankLetterResult.fileObjects];
      const files = [...authLetterResult.fileMetadata, ...bankLetterResult.fileMetadata];

      const hasNewFiles = fileObjects.length > 0;

      if (hasNewFiles) {
        const response = await getProviderManagementDocumentsUrlAPI({ network_id: data.provider_network_id, files }, getAuthToken());

        if (!response.successful_files || response.successful_files.length === 0) {
          const errorMessage = response.errors?.map((err) => err.message).join("; ") || "No files were successfully processed";
          showAlert({
            type: "error",
            style: "banner",
            message: errorMessage,
          });
          return;
        }

        const fileMap = new Map<string, File>();
        fileObjects.forEach((obj) => fileMap.set(obj.fileName, obj.file));

        let successfulUploads = 0;
        const totalFilesToUpload = response.successful_files.length;

        for (const fileResult of response.successful_files) {
          try {
            const file = fileMap.get(fileResult.file_name);
            if (file && fileResult.post) {
              await uploadFileToS3(file, fileResult.post);
              successfulUploads++;
            }
          } catch (error) {
            console.error(`Error uploading file ${fileResult.file_name}:`, error);
          }
        }

        if (successfulUploads === totalFilesToUpload) {
          showAlert({
            type: "success",
            style: "banner",
            message: "Successfully uploaded file",
          });
        } else if (successfulUploads > 0) {
          showAlert({
            type: "success",
            style: "banner",
            message: `${successfulUploads} of ${totalFilesToUpload} documents uploaded successfully`,
          });
        } else {
          showAlert({
            type: "error",
            style: "banner",
            message: "Failed to upload documents",
          });
          return;
        }
      }

      // --- Payment Preference Logic ---
      const remittanceAddress = {} as DeepMutable<
        Pick<
          ProviderManagement.PaymentPreferenceSchema,
          | "remittance_address_line_1"
          | "remittance_address_line_2"
          | "remittance_address_city"
          | "remittance_address_state"
          | "remittance_address_zip_code"
        >
      >;
      if (data.remittance_type === ProviderManagement.REMITTANCE_TYPE_VALUES.PAPER) {
        remittanceAddress.remittance_address_line_1 = data?.remit?.check ? provider.mailing_address_line_1 : data.remit?.street_address;
        remittanceAddress.remittance_address_line_2 = data?.remit?.check ? provider.mailing_address_line_2 : data.remit?.address_line;
        remittanceAddress.remittance_address_city = data?.remit?.check ? provider.mailing_address_city : data.remit?.city;
        remittanceAddress.remittance_address_state = data?.remit?.check ? provider.mailing_address_state : data.remit?.state;
        remittanceAddress.remittance_address_zip_code = data?.remit?.check ? provider.mailing_address_zip_code : data.remit?.zip_code;
      }

      const payload = {
        provider_network_id: data.provider_network_id,
        network_enrollment_status: "in_review",
        payment_type: data.payment_type,
        financial_institution_name: data.financial_institutions?.name,
        financial_institution_address_line_1: data.financial_institutions?.street_address,
        financial_institution_address_line_2: data.financial_institutions?.address_line,
        financial_institution_address_city: data.financial_institutions?.city,
        financial_institution_address_state: data.financial_institutions?.state,
        financial_institution_address_zip_code: data.financial_institutions?.zip_code,
        financial_institution_contact_first_name: data.financial_institutions?.first_name,
        financial_institution_contact_last_name: data.financial_institutions?.last_name,
        financial_institution_phone: data.financial_institutions?.phone_number,
        financial_institution_fax: data.financial_institutions?.fax_number,
        ach_account_type: data.type_account,
        ach_routing_number: data.routing_number,
        ach_bank_account_number: data.bank_account_number,
        account_owner_first_name: data.account_owner_first_name,
        account_owner_last_name: data.account_owner_last_name,
        // decision: '', // TODO back
        remittance_recipient_email_addresses: data.email_address,
        remittance_recipient_sftp_delivery_targets: data.sftp_delivery_targets,
        remittance_type: data.remittance_type,
        transaction_validation_claim_1_number: data.transaction_validation_claim_1_number,
        transaction_validation_claim_1_paid_amount: data.transaction_validation_claim_1_paid_amount,
        transaction_validation_claim_2_number: data.transaction_validation_claim_2_number,
        transaction_validation_claim_2_paid_amount: data.transaction_validation_claim_2_paid_amount,
        ...remittanceAddress,
      };

      // Don't save redacted bank account numbers
      for (const field of ["ach_routing_number", "ach_bank_account_number"]) {
        if (payload[field] && payload[field].includes("*")) {
          delete payload[field];
        }
      }

      const newPreference = await createPaymentPreferenceAPI({ provider_network_id: data.provider_network_id }, getAuthToken());
      const updateResponse = await updatePaymentPreferenceAPI({ ...payload, id: newPreference.id }, getAuthToken());
      if (updateResponse) {
        setPaymentPreference({ ...updateResponse, ...payload });
      }
      setPaymentPreference({ ...paymentPreference, ...payload });
      navigate(`${getThemeURL("ACCOUNT")}?tab=payment`);
    } catch (error) {
      console.error(error);
      showAlert({
        type: "error",
        style: "banner",
        message: "Failed to save provider details. Please try again later.",
      });
      // analytics log validate fail
      logEvent(ANALYTIC_EVENTS.ERRORS.providerManagementUpdate);
      return;
    }

    logEvent(ANALYTIC_EVENTS.PROVIDER_MANAGEMENT.update_success);
  };

  const [discardChangeModal, setDiscardChangeModal] = useState(false);
  const discardChangeModalHandler = (approved: boolean) => {
    setDiscardChangeModal(false);
    if (approved) {
      form.reset();
      navigate(`${getThemeURL("ACCOUNT")}?tab=payment`);
    }
  };
  const cancelHandler = () => {
    if (!deepEqual(provider, form.getValues())) {
      setDiscardChangeModal(true);
    } else {
      form.reset();
      navigate(`${getThemeURL("ACCOUNT")}?tab=payment`);
    }
  };

  return (
    <Stack spacing="large">
      <Stack spacing="small" alignment="left">
        <Shelf spacing="mini">
          <Icon color={hexToRgbColor(primary_color)} name="arrow-left" />
          <Link onClick={cancelHandler} to={`${getThemeURL("ACCOUNT")}?tab=payment`}>
            Network & Payment Preferences
          </Link>
        </Shelf>
        <Title>New Network</Title>
      </Stack>
      {(isLoading || isNetworkLoading || isNetworkConfigLoading) && (
        <Stack alignment="center"><Spinner size="large" /></Stack>
      )}
      {!isLoading && (
        <Form form={form} onSubmit={submitHandler}>
          <Stack spacing="large">
            <Panel>
              <Stack spacing="large">
                <Stack>
                  <Heading>Network Selection</Heading>
                  <Grid>
                    <Grid.Span columns={sixFull}>
                      <ProviderNetworkDropdown name="provider_network_id" required remainingNetworks={remainingNetworks} readOnly={false} />
                    </Grid.Span>
                  </Grid>
                </Stack>
                <Stack>
                  <Heading>Payment & Remittance</Heading>
                  <Stack>
                    <Subheading>Payment Type</Subheading>
                    <Grid>
                      <Grid.Span columns={twelve}>
                        <RadioGroup.Field
                          name="payment_type"
                          testId="payment_type"
                          orientation="horizontal"
                          hideLabel
                          readOnly={false}
                          defaultValue={ProviderManagement.PAYMENT_TYPE_VALUES.ACH}
                        >
                          <RadioGroup.Option value={ProviderManagement.PAYMENT_TYPE_VALUES.ACH}>Bank Account/ACH</RadioGroup.Option>
                          <RadioGroup.Option value={ProviderManagement.PAYMENT_TYPE_VALUES.CHECK}>Check</RadioGroup.Option>
                        </RadioGroup.Field>
                      </Grid.Span>
                      {/* This next portion is commented as per ad-hoc design detailed in https://capitalrx.atlassian.net/browse/CAP-80855 since we'll need to bring it back for VCC */}
                      {/* <Message appearance={Messages.Informational} visible={paymentType === ProviderManagement.PAYMENT_TYPE_VALUES.CHECK}>Checks will be sent to the Provider’s mailing address</Message> */}
                      {/* {paymentType === ProviderManagement.PAYMENT_TYPE_VALUES.CHECK && <Message appearance={Messages.Informational} visible>{ProviderManagement.PAYMENT_TYPE_INFORMATIONAL_MESSAGES.CHECK}</Message>}
                      {paymentType === ProviderManagement.PAYMENT_TYPE_VALUES.ACH && <Message appearance={Messages.Informational} visible>{ProviderManagement.PAYMENT_TYPE_INFORMATIONAL_MESSAGES.ACH}</Message>} */}
                      <Message appearance={Messages.Informational} visible>
                        {paymentType === ProviderManagement.PAYMENT_TYPE_VALUES.CHECK
                          ? ProviderManagement.PAYMENT_TYPE_INFORMATIONAL_MESSAGES.CHECK
                          : ProviderManagement.PAYMENT_TYPE_INFORMATIONAL_MESSAGES.ACH}
                      </Message>
                    </Grid>
                  </Stack>
                  <Stack>
                    {paymentType === ProviderManagement.PAYMENT_TYPE_VALUES.ACH && (
                      <Stack>
                        <Subheading>Financial Information</Subheading>
                        <Grid>
                          <Grid.Span columns={twelve}>
                            <Textbox.Field
                              label="Financial Institution’s Name"
                              name="financial_institutions.name"
                              testId="financial_institutions_name"
                              required
                            />
                          </Grid.Span>
                          <Grid.Break />
                          <Grid.Spread columns={twelve}>
                            <Textbox.Field
                              label="Street Address"
                              name="financial_institutions.street_address"
                              testId="financial_institutions_street_address"
                              required
                            />
                            <Textbox.Field
                              label="Address Line 2"
                              name="financial_institutions.address_line"
                              testId="financial_institutions_address_line"
                            />
                          </Grid.Spread>
                          <Grid.Span columns={sixFull}>
                            <Textbox.Field label="City" name="financial_institutions.city" testId="financial_institutions_city" required />
                          </Grid.Span>
                          <Grid.Spread columns={threeFull}>
                            <Dropdown.Field
                              label="State"
                              name="financial_institutions.state"
                              testId="financial_institutions_state"
                              required
                              searchable
                            >
                              {Object.entries(states).map(([full, abbreviation]) => {
                                return (
                                  <Dropdown.Option key={abbreviation} value={abbreviation}>
                                    {full}
                                  </Dropdown.Option>
                                );
                              })}
                            </Dropdown.Field>
                            <Textbox.Field
                              label="Zip Code"
                              name="financial_institutions.zip_code"
                              testId="financial_institutions_zip_code"
                              validator={(value) => validate.isZipCode(value)}
                              required
                            />
                          </Grid.Spread>
                          <Grid.Spread columns={sixFull}>
                            <Textbox.Field
                              label="Contact First Name"
                              name="financial_institutions.first_name"
                              testId="financial_institutions_first_name"
                              required
                            />
                            <Textbox.Field
                              label="Contact Last Name"
                              name="financial_institutions.last_name"
                              testId="financial_institutions_last_name"
                              required
                            />
                            <Textbox.Field
                              label="Phone Number"
                              name="financial_institutions.phone_number"
                              testId="financial_institutions_phone_number"
                              instructions="123-456-7890 x 123"
                              validator={(value) => validatePhoneNumber(value)}
                              required
                            />
                            <Textbox.Field
                              label="Fax Number"
                              name="financial_institutions.fax_number"
                              testId="financial_institutions_fax_number"
                              instructions="123-456-7890 x 123"
                              validator={(value) => validatePhoneNumber(value)}
                              required
                            />
                          </Grid.Spread>
                        </Grid>
                      </Stack>
                    )}
                    {paymentType === ProviderManagement.PAYMENT_TYPE_VALUES.ACH && (
                      <FieldsBankAccount readOnly={false} typeForm={"create"} />
                    )}
                    <FieldsRemittanceType provider={provider} />
                  </Stack>
                </Stack>
                {provider?.type === ProviderManagement.PROVIDER_TYPE_VALUES.MEDICAL && (
                  <Stack>
                    <FieldsTransactionValidation readOnly={false} />
                  </Stack>
                )}
                <Stack>
                  <Heading>Documents</Heading>
                  <Stack spacing="large">
                    <Stack>
                      <Semibold>Required Documents:</Semibold>
                      <Stack spacing="none">
                        <Regular>1. Authorization Letter for EFT - A signed letter authorizing electronic funds transfer.</Regular>
                        <Regular>2. Bank Letterhead OR Voided Check - A document from your bank confirming account details.</Regular>
                      </Stack>
                    </Stack>
                    <Stack>
                      <Subheading>Authorization Letter</Subheading>
                      <Grid>
                        <Grid.Spread columns={twelve}>
                          <FileUpload.Field
                            name="authorization_letter.files"
                            allowMultiple={true}
                            allowedExtensions={[".docx", ".pdf", ".jpeg", ".jpg", ".png"]}
                            testId="authorization_letter_files"
                            label="Select File(s)"
                            maxSize={4500000}
                            required
                          />
                          <TextArea.Field label="Notes" testId="authorization_letter_notes" name="authorization_letter.notes" lines={6} />
                        </Grid.Spread>
                      </Grid>
                    </Stack>
                    <Stack>
                      <Subheading>Bank Letterhead or Voided Check</Subheading>
                      <Grid>
                        <Grid.Span columns={sixFull}>
                          <Dropdown.Field
                            label="Type"
                            name="bank_letterhead_voided_check.type"
                            testId="bank_letterhead_voided_check_type"
                            required
                          >
                            <Dropdown.Option value={ProviderManagement.DOCUMENT_TYPE_VALUES.BANK_LETTERHEAD}>
                              Bank Letterhead
                            </Dropdown.Option>
                            <Dropdown.Option value={ProviderManagement.DOCUMENT_TYPE_VALUES.VOIDED_CHECK}>Voided Check</Dropdown.Option>
                          </Dropdown.Field>
                        </Grid.Span>
                        <Grid.Break />
                        <Grid.Spread columns={twelve}>
                          <FileUpload.Field
                            name="bank_letterhead_voided_check.files"
                            allowMultiple={true}
                            allowedExtensions={[".docx", ".pdf", ".jpeg", ".jpg", ".png"]}
                            testId="bank_letterhead_voided_check_files"
                            label="Select File(s)"
                            maxSize={4500000}
                            required
                          />
                          <TextArea.Field
                            label="Notes"
                            testId="bank_letterhead_voided_check_notes"
                            name="bank_letterhead_voided_check.notes"
                            lines={6}
                          />
                        </Grid.Spread>
                      </Grid>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </Panel>
            <Shelf spacing="small">
              <SubmitButton appearance={getThemeButton("primary", primary_color, primary_hover_color)}>Submit for Review</SubmitButton>
              <Button appearance={getThemeButton("secondary", primary_color, secondary_hover_color)} onClick={cancelHandler}>
                Cancel
              </Button>
              <Shelf.Spacer />
            </Shelf>
          </Stack>
        </Form>
      )}
      <Modal.Approve
        title="Discard Changes"
        onClose={discardChangeModalHandler}
        visible={discardChangeModal}
        approveButtonAppearance={Buttons.Destructive.Primary.Large}
        denyContent="Cancel"
        approveContent="Discard"
        testId="edit_contact_information_discard"
      >
        <Body>Unsaved changes will be lost if you proceed</Body>
      </Modal.Approve>
    </Stack>
  );
};
